/**
 * AdminDashboardLanding (STORY-008-006). /admin index: a welcome + quick-links
 * to the sections the user can access (the real dashboard is STORY-009-005 at
 * /admin/dashboard). --g-* tokens; @garden/ui Card.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Card } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ADMIN_NAV } from "@/features/admin/navConfig";
import { useMe } from "@/features/settings/api";

export function AdminDashboardLanding(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data: profile } = useMe();
  const permissions: string[] = profile?.permissions ?? [];
  const links = ADMIN_NAV.flatMap((g) => g.items).filter((it) => permissions.includes(it.requiredPermission));

  return (
    <div data-testid="admin-landing" className="mx-auto flex max-w-4xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{t("landing.title")}</h1>
        <p className="text-sm text-[var(--g-secondary)]">{t("landing.subtitle")}</p>
      </header>
      {links.length === 0 ? (
        <p data-testid="admin-landing-empty" className="text-sm text-[var(--g-secondary)]">{t("landing.empty")}</p>
      ) : (
        <ul role="list" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {links.map((it) => {
            const IconCmp = it.icon;
            return (
              <li key={it.id}>
                <Link
                  to={it.to}
                  data-testid={`admin-landing-link-${it.id}`}
                  className="block rounded-[var(--g-radius)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]"
                >
                  <Card className="flex min-h-[88px] items-center gap-3 p-4 transition-colors hover:bg-[var(--g-elevated)]">
                    <IconCmp size={20} strokeWidth={1.75} aria-hidden className="text-[var(--g-primary)]" />
                    <span className="text-sm font-medium text-foreground">{t(it.labelKey)}</span>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AdminDashboardLanding;
